import { supabase } from '../supabaseClient';
import {
  createDefaultClassLessonAlarmCustomSchedule,
  emitClassLessonAlarmCustomScheduleChanged,
  normalizeClassLessonAlarmCustomSchedule,
  type ClassLessonAlarmCustomSchedule,
} from '../utils/classLessonAlarmCustomSchedule';
import {
  loadClassLessonAlarmCustomScheduleCache,
  saveClassLessonAlarmCustomScheduleCache,
} from '../utils/classLessonAlarmCustomScheduleCache';

function requireWorkspaceId(workspaceId: string): string {
  const ws = workspaceId.trim();
  if (!ws) throw new Error('워크스페이스를 선택해 주세요.');
  return ws;
}

async function requireUserId(): Promise<string> {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw new Error(error.message);
  const uid = data.user?.id;
  if (!uid) throw new Error('로그인이 필요합니다.');
  return uid;
}

function isMissingTableError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('workspace_class_lesson_alarm_custom_schedules') &&
    (lower.includes('schema cache') || lower.includes('does not exist') || lower.includes('could not find'))
  );
}

function toDbPayload(schedule: ClassLessonAlarmCustomSchedule): {
  mode: string;
  overrides: Record<string, { time: string; offset: string }>;
} {
  const normalized = normalizeClassLessonAlarmCustomSchedule(schedule);
  return {
    mode: normalized.mode,
    overrides: normalized.overrides,
  };
}

export async function getClassLessonAlarmCustomSchedule(
  workspaceId: string,
  options?: { forceRefresh?: boolean }
): Promise<ClassLessonAlarmCustomSchedule> {
  const ws = requireWorkspaceId(workspaceId);
  const empty = createDefaultClassLessonAlarmCustomSchedule();
  if (!options?.forceRefresh) {
    const cached = loadClassLessonAlarmCustomScheduleCache(ws);
    if (cached.found) return cached.schedule;
  }

  try {
    const uid = await requireUserId();
    const { data, error } = await supabase
      .from('workspace_class_lesson_alarm_custom_schedules')
      .select('mode, overrides')
      .eq('user_id', uid)
      .eq('workspace_id', ws)
      .maybeSingle();

    if (error) {
      if (isMissingTableError(error.message)) {
        console.warn('[class-lesson-alarm] custom schedule 테이블 없음:', error.message);
        return empty;
      }
      throw new Error(error.message);
    }

    const schedule = normalizeClassLessonAlarmCustomSchedule({
      v: 1,
      mode: data?.mode,
      overrides: data?.overrides,
    });
    saveClassLessonAlarmCustomScheduleCache(ws, schedule);
    return schedule;
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    if (isMissingTableError(message)) {
      console.warn('[class-lesson-alarm] custom schedule 조회 실패:', message);
      return empty;
    }
    throw e;
  }
}

export async function saveClassLessonAlarmCustomSchedule(
  workspaceId: string,
  schedule: ClassLessonAlarmCustomSchedule
): Promise<void> {
  const ws = requireWorkspaceId(workspaceId);
  const uid = await requireUserId();
  const normalized = normalizeClassLessonAlarmCustomSchedule(schedule);
  const db = toDbPayload(normalized);
  const payload = {
    user_id: uid,
    workspace_id: ws,
    mode: db.mode,
    overrides: db.overrides,
    updated_at: new Date().toISOString(),
  };

  const { data: existing, error: existingErr } = await supabase
    .from('workspace_class_lesson_alarm_custom_schedules')
    .select('id')
    .eq('user_id', uid)
    .eq('workspace_id', ws)
    .maybeSingle();
  if (existingErr) {
    if (isMissingTableError(existingErr.message)) {
      throw new Error(
        '커스텀 알람 스케줄 테이블이 아직 준비되지 않았습니다. 잠시 후 다시 시도하거나 관리자에게 문의해 주세요.'
      );
    }
    throw new Error(existingErr.message);
  }

  if (existing?.id) {
    const { error } = await supabase
      .from('workspace_class_lesson_alarm_custom_schedules')
      .update(payload)
      .eq('id', existing.id);
    if (error) {
      if (isMissingTableError(error.message)) {
        throw new Error(
          '커스텀 알람 스케줄 테이블이 아직 준비되지 않았습니다. 잠시 후 다시 시도하거나 관리자에게 문의해 주세요.'
        );
      }
      throw new Error(error.message);
    }
  } else {
    const { error } = await supabase.from('workspace_class_lesson_alarm_custom_schedules').insert({
      ...payload,
      created_at: payload.updated_at,
    });
    if (error) {
      if (isMissingTableError(error.message)) {
        throw new Error(
          '커스텀 알람 스케줄 테이블이 아직 준비되지 않았습니다. 잠시 후 다시 시도하거나 관리자에게 문의해 주세요.'
        );
      }
      throw new Error(error.message);
    }
  }

  saveClassLessonAlarmCustomScheduleCache(ws, normalized);
  emitClassLessonAlarmCustomScheduleChanged(ws);
}
